/**
 * AnalyticsConsentBanner — UI v2.0 §7 mandatory consent layer.
 *
 * Bottom-pinned strip that appears once per browser until the user
 * chooses.  No tracking happens before consent (track() is a no-op
 * until then), so this banner does not block the user from anything.
 *
 * Per spec, opt-out leaves educational access intact.
 */
import { useEffect, useState } from "react";
import { getConsent, setConsent } from "../lib/analytics";

export default function AnalyticsConsentBanner() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Defer one frame so we don't flash during initial paint.
    const t = setTimeout(() => {
      if (getConsent() === null) setOpen(true);
    }, 600);
    return () => clearTimeout(t);
  }, []);

  if (!open) return null;

  async function choose(yes) {
    setBusy(true);
    await setConsent(yes);
    setBusy(false);
    setOpen(false);
  }

  return (
    <div
      className="fixed bottom-24 md:bottom-28 right-4 z-50 w-[min(420px,calc(100%-2rem))]"
      data-testid="analytics-consent-banner"
      role="dialog"
      aria-live="polite"
    >
      <div className="glass-card rounded-2xl p-4 sm:p-5 flex flex-col gap-3 shadow-2xl shadow-black/40">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#6B8F71] mb-1">
            Educational analytics · optional
          </p>
          <p className="text-xs text-white/80 leading-relaxed">
            We&apos;d like to log <em>de-identified</em> learning events
            (module viewed, time on section, quiz correct/incorrect) to
            improve the platform.{" "}
            <span className="text-white">No personal data, no patient
            identifiers, no external browsing.</span>
          </p>
        </div>
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={() => choose(false)}
            disabled={busy}
            data-testid="analytics-consent-no"
            className="btn-ghost text-xs"
          >
            No, just learn
          </button>
          <button
            type="button"
            onClick={() => choose(true)}
            disabled={busy}
            data-testid="analytics-consent-yes"
            className="btn-glow text-xs"
          >
            Yes, help research
          </button>
        </div>
      </div>
    </div>
  );
}
